import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {CaseHal} from '../../models/HAL/case-hal.model';
import {DatePipe, NgClass} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {DialogService} from '../../services/dialog.service';
import {DocumentHal, PagedDocumentHal} from '../../models/HAL/document-hal.model';
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {ReactiveFormsModule} from "@angular/forms";
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {SessionService} from '../../services/session.service';
import {MatMenuModule} from '@angular/material/menu';
import {MatTooltipModule} from '@angular/material/tooltip';
import {SnackBarService} from '../../services/snackbar.service';
import {DocumentDtoModel} from '../../models/DTO/DocumentDto.model';
import {catchError, concatMap, finalize, from, last, map, merge, Observable, of, reduce, Subscription, tap} from 'rxjs';
import {DragToUploadDirective} from '../../directives/drag-to-upload.directive';
import {
  DynamicConfirmationDialogComponent
} from '../../components/dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';
import {AddFormsDialogComponent} from '../../components/add-forms-dialog/add-forms-dialog.component';
import {DocumentHelperService} from '../../services/document.helper.service';
import {AllowedFileTypeService} from '../../services/allowed-file-type.service';
import {AdditionalUserInfo} from '../../components/smart-text/additional-user-info.component';
import {RequestInformationDisabledReasonEnum} from '../../enums/request-information-disabled-reason.enum';
import {SelectionActionsBarComponent} from '../../components/selection-actions-bar/selection-actions-bar.component';
import {SelectionActionComponent} from '../../components/selection-action/selection-action.component';
import {buildCaseZipFilename} from '../../utils/case-zip-filename';
import {OverflowButton} from '../../models/overflow-button.model';
import {CommentContextService} from '../../services/comment-context.service';
import {TableSelectionCellComponent} from '../../components/table-selection-cell/table-selection-cell.component';
import {MoveCaseComponent} from '../../components/move-case/move-case.component';
import {Team} from '../../models/HAL/team-hal.model';

const COMPLETION_DOCUMENT_TYPE = 'COMPLETION_DOCUMENT';

@Component({
  selector: 'app-case-detail',
  imports: [
    DatePipe,
    TranslatePipe,
    MatButton,
    MatIcon,
    ReactiveFormsModule,
    MatPaginator,
    MatProgressSpinner,
    MatTableModule,
    NgClass,
    MatIconButton,
    MatMenuModule,
    MatTooltipModule,
    DragToUploadDirective,
    SelectionActionsBarComponent,
    SelectionActionComponent,
    AdditionalUserInfo,
    TableSelectionCellComponent
  ],
  templateUrl: './case-detail.component.html',
  styleUrl: './case-detail.component.scss'
})
export class CaseDetailComponent implements AfterViewInit, OnDestroy {

  private files: File[] = [];
  protected loadingDocuments: boolean = false;
  protected dataSource: MatTableDataSource<DocumentHal> = new MatTableDataSource<DocumentHal>([]);
  protected displayedColumns: string[] = ['selection', 'name', 'state', 'lastAction', 'actions'];
  protected caseHal: CaseHal;
  protected pagedDocumentHal: PagedDocumentHal = new PagedDocumentHal({
    _embedded: {
      documents: []
    },
    page: {
      size: 0,
      totalElements: 0,
      totalPages: 0
    }
  });
  protected documentHals: DocumentHal[] = [];
  protected selectedDocuments: string[] = [];
  protected uploading: boolean = false;
  protected hiddenButtons = new Set<string>();
  private uploadAsCompletionDocument = false;
  protected hiddenMenuButtons: OverflowButton[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('titleAndButtonContainer') private readonly titleAndButtonContainerRef!: ElementRef<HTMLElement>;
  @ViewChild('breadcrumbEl') private readonly breadcrumbRef!: ElementRef<HTMLElement>;
  @ViewChild('completeCaseButton', {read: ElementRef}) private readonly completeCaseButtonRef!: ElementRef<HTMLElement>;
  @ViewChild('fileUpload') private readonly fileUpload?: ElementRef<HTMLInputElement>;
  @ViewChildren('measureButton', {read: ElementRef}) private readonly measureButtons!: QueryList<ElementRef<HTMLElement>>;

  private resizeObserver?: ResizeObserver;
  private documentRequestId: number = 0;
  private destroyed: boolean = false;
  private caseRequestId: number = 0;
  private readonly caseRefreshSubscription: Subscription;

  protected overflowButtons: OverflowButton[] = [];

  constructor(
    activatedRoute: ActivatedRoute,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly httpClient: HttpClient,
    private readonly dialogService: DialogService,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly snackBarService: SnackBarService,
    private readonly translateService: TranslateService,
    private readonly documentHelperService: DocumentHelperService,
    private readonly ngZone: NgZone,
    private readonly commentContextService: CommentContextService
  ) {
    this.loadingDocuments = true;
    this.caseHal = new CaseHal(activatedRoute.snapshot.data['case']);
    this.commentContextService.setPanelHal(this.caseHal);
    this.caseRefreshSubscription = this.commentContextService.caseRefreshRequested.subscribe(() => {
      this.updateDocumentsHal(this.paginator);
      this.refreshCase();
    });
    this.initOverflowButtons();
  }

  ngAfterViewInit(): void {
    this.updateDocumentsHal(this.paginator);
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.caseRefreshSubscription.unsubscribe();
    this.commentContextService.setPanelHal(null);
  }

  private initOverflowButtons(): void {
    this.overflowButtons = [
      {
        key: 'disableAutomation',
        icon: '',
        labelKey: 'caseDetail.buttons.deactivateAutomation.label',
        buttonType: 'mat-stroked-button',
        action: () => this.openDisableAutomationModal(),
        visible: () => this.caseHal._links.hasLink('disableAutomation'),
        disabled: () => false
      },
      {
        key: 'move',
        icon: 'move_item',
        labelKey: 'caseDetail.buttons.moveCase.label',
        buttonType: 'mat-button',
        action: () => this.openMoveCaseModal(),
        visible: () => this.caseHal._links.hasLink('move'),
        disabled: () => false
      },
      {
        key: 'requestInformation',
        icon: 'forum',
        labelKey: 'caseDetail.buttons.requestInformation.label',
        buttonType: 'mat-button',
        action: () => this.requestInformation(),
        visible: () => !this.isAutomatedCase() && this.showCaseRequestInformationButton(),
        disabled: () => !this.caseHal._links.hasLink('requestInformation')
      },
      {
        key: 'sign',
        icon: 'signature',
        labelKey: 'caseDetail.buttons.startSignatureProcess.label',
        buttonType: 'mat-button',
        action: () => this.startSignatureProcess(),
        visible: () => !this.isAutomatedCase() && this.caseHal._links.hasLink('sign'),
        disabled: () => false
      },
      {
        key: 'onlineIdentification',
        icon: 'person_search',
        labelKey: 'caseDetail.buttons.onlineIdentification.label',
        buttonType: 'mat-button',
        action: () => this.startOnlineIdentification(),
        visible: () => !this.isAutomatedCase() && this.caseHal._links.hasLink('onlineIdentification'),
        disabled: () => false
      },
      {
        key: 'uploadCompletionDocument',
        icon: 'upload_file',
        labelKey: 'caseDetail.buttons.uploadCompletionDocument.label',
        buttonType: 'mat-button',
        action: () => this.triggerCompletionDocumentUpload(),
        visible: () => this.isCaseInProcessing(),
        disabled: () => this.uploading || !this.canUploadDocuments()
      }
    ];
  }

  private setupResizeObserver(): void {
    const container = this.titleAndButtonContainerRef?.nativeElement;
    if (!container) return;
    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.recalculateOverflow());
      this.resizeObserver.observe(container);
    });
    this.measureButtons.changes.subscribe(() => this.recalculateOverflow());
    setTimeout(() => this.recalculateOverflow());
  }

  private recalculateOverflow(): void {
    const container = this.titleAndButtonContainerRef?.nativeElement;
    const breadcrumb = this.breadcrumbRef?.nativeElement;
    const completeBtn = this.completeCaseButtonRef?.nativeElement;
    if (!container || !breadcrumb) return;

    const containerWidth = container.clientWidth;
    const completeBtnWidth = completeBtn ? completeBtn.offsetWidth + 8 : 0;
    const overflowIconWidth = 48;
    const gap = 8;

    // Measure the breadcrumb's natural (untruncated) width by summing children's scrollWidths
    const breadcrumbWidth = breadcrumb.offsetWidth;

    const buttonWidths = this.getButtonMeasurements();
    const visibleButtons = this.overflowButtons.filter(b => b.visible());

    const newHidden = new Set<string>();

    let usedWidth = breadcrumbWidth + completeBtnWidth + gap;
    for (const btn of visibleButtons) {
      usedWidth += (buttonWidths.get(btn.key) ?? 0) + gap;
    }


    if (usedWidth > containerWidth) {
      usedWidth += overflowIconWidth;
      for (const btn of visibleButtons) {
        if (usedWidth <= containerWidth) break;
        const btnWidth = (buttonWidths.get(btn.key) ?? 0) + gap;
        newHidden.add(btn.key);
        usedWidth -= btnWidth;
      }
    }

    if (this.setsEqual(this.hiddenButtons, newHidden)) return;

    this.ngZone.run(() => {
      this.hiddenButtons = newHidden;
      this.hiddenMenuButtons = this.overflowButtons.filter(b => b.visible() && newHidden.has(b.key));
    });
  }

  private setsEqual(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) return false;
    for (const item of a) {
      if (!b.has(item)) return false;
    }
    return true;
  }

  private getButtonMeasurements(): Map<string, number> {
    const map = new Map<string, number>();
    if (!this.measureButtons) return map;
    this.measureButtons.forEach(ref => {
      const key = ref.nativeElement.dataset['key'];
      if (key) {
        map.set(key, ref.nativeElement.offsetWidth);
      }
    });
    return map;
  }

  /**
   * The primary action is a single slot. Where the tenant uses the processing phase the slot reads "confirm
   * completeness" throughout, even while the transition is not possible yet — that is the step this tenant's
   * lifecycle leads to. Only a case in PROCESSING, and a completed one, offer completing instead. Whether the action
   * can actually be triggered stays with the backend: the {@code startProcessing} link decides that.
   */
  protected showConfirmCompletenessCta(): boolean {
    return !!this.caseHal.processingPhaseEnabled
      && !this.isCaseInProcessing()
      && !this.isAutomatedCase()
      && this.caseHal.state !== 'COMPLETED';
  }

  /**
   * The tooltip explains the two states of the call to action: what the click will do, and — the case that needs it —
   * why it is not clickable. The precondition is the backend's link, never a rebuilt condition.
   */
  protected getConfirmCompletenessTooltipKey(): string {
    return this.caseHal._links.hasLink('startProcessing')
      ? 'caseDetail.buttons.confirmCompleteness.tooltip.enabled'
      : 'caseDetail.buttons.confirmCompleteness.tooltip.disabled';
  }

  protected isCaseInProcessing(): boolean {
    return this.caseHal?.state === 'PROCESSING';
  }

  /**
   * A locked document is recognised by the absence of its mutation links: the backend stops emitting them once the
   * document carries a lock. Completed documents and automated cases lose the same links for their own reasons, so
   * both are ruled out first — otherwise their rows would be treated as locked.
   */
  protected isDocumentLocked(document: DocumentHal): boolean {
    if (this.isAutomatedCase() || document.state === 'COMPLETED' || this.caseHal?.state === 'COMPLETED') {
      return false;
    }
    return !document._links?.hasLink('delete');
  }

  protected isCompletionDocument(document: DocumentHal): boolean {
    return document.type === COMPLETION_DOCUMENT_TYPE;
  }

  protected triggerCompletionDocumentUpload(): void {
    this.uploadAsCompletionDocument = true;
    this.fileUpload?.nativeElement.click();
  }

  protected confirmCompleteness(): void {
    const startProcessingLink = this.caseHal._links.getLink('startProcessing')?.href;
    if (!startProcessingLink) {
      return;
    }
    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'caseDetail.confirmCompleteness.title',
        message: 'caseDetail.confirmCompleteness.message',
        confirmButtonText: 'caseDetail.confirmCompleteness.buttons.confirm',
        cancelButtonText: 'caseDetail.confirmCompleteness.buttons.cancel'
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.startProcessing(startProcessingLink);
      }
    });
  }

  // The endpoint answers without the detail view links, so the case is re-read instead of using the response body.
  private startProcessing(startProcessingLink: string): void {
    this.httpClient.post<CaseHal>(startProcessingLink, {}).subscribe({
      next: () => {
        this.snackBarService.open('caseDetail.snackbars.confirmCompletenessSuccess', 3);
        this.updateDocumentsHal(this.paginator);
        this.refreshCase();
      },
      error: () => this.snackBarService.open('caseDetail.snackbars.confirmCompletenessError', 3, 'error')
    });
  }

  protected isButtonHidden(key: string): boolean {
    return this.hiddenButtons.has(key);
  }

  protected updateDocumentsHal(pageEvent: PageEvent) {
    const requestId = ++this.documentRequestId;
    this.loadingSpinnerService.hide();
    this.loadingDocuments = true;
    const url = new URL(this.caseHal._links.getLink('getDocuments')!.href);
    url.searchParams.set('size', pageEvent?.pageSize.toString() ?? '20');
    url.searchParams.set('page', pageEvent?.pageIndex.toString() ?? '0');
    url.searchParams.set('paged', 'true');
    this.httpClient.get<PagedDocumentHal>(url.href).subscribe({
      next: (pagedDocumentHal: PagedDocumentHal) => {
        if (requestId !== this.documentRequestId) {
          return;
        }
        if (pagedDocumentHal._embedded) {
          this.documentHals = pagedDocumentHal._embedded?.documents!;
          this.pagedDocumentHal = new PagedDocumentHal(pagedDocumentHal);
          this.dataSource = new MatTableDataSource<DocumentHal>(this.pagedDocumentHal._embedded?.documents);
        } else {
          this.documentHals = [];
          this.pagedDocumentHal = new PagedDocumentHal({
            _embedded: {
              documents: []
            },
            page: {
              size: 0,
              totalElements: 0,
              totalPages: 0
            }
          });
          this.dataSource = new MatTableDataSource<DocumentHal>(this.documentHals);
        }
        const visibleDocumentIds = new Set(this.documentHals.map(document => document.identification));
        this.selectedDocuments = this.selectedDocuments.filter(documentId => visibleDocumentIds.has(documentId));
        this.loadingDocuments = false;
      },
      error: () => {
        if (requestId !== this.documentRequestId) {
          return;
        }
        this.loadingDocuments = false;
        this.snackBarService.open('caseDetail.snackbars.loadError', 3, 'error');
      }
    });
  }

  protected requestInformation() {
    this.loadingSpinnerService.show();
    this.router.navigate([this.caseHal._links.getLink('requestInformation')?.getRoute()]).then();
  }

  protected startSignatureProcess() {
    this.loadingSpinnerService.show();
    this.router.navigate([this.caseHal._links.getLink('sign')?.getRoute()]).then();
  }

  protected startOnlineIdentification(): void {
    void this.router.navigate([this.caseHal._links.getLink('onlineIdentification')?.getRoute()]);
  }

  protected isAutomatedCase(): boolean {
    return this.caseHal.state?.toUpperCase() === 'AUTOMATED';
  }

  /**
   * Adding an ordinary information document is not offered in the processing phase: the dossier is frozen, and such a
   * document would stay unlocked and never reach COMPLETED. The completion document upload has its own action.
   */
  protected canAddRegularDocuments(): boolean {
    return this.canUploadDocuments() && !this.isCaseInProcessing();
  }

  protected canUploadDocuments(): boolean {
    return !!this.caseHal?._links.getLink('addDocuments');
  }

  protected openDisableAutomationModal() {
    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'caseDetail.disableAutomationDialog.title',
        message: 'caseDetail.disableAutomationDialog.message',
        confirmButtonText: 'caseDetail.disableAutomationDialog.confirm',
        cancelButtonText: 'caseDetail.disableAutomationDialog.cancel'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.disableAutomation();
      }
    });
  }

  private disableAutomation() {
    const disableAutomationLink = this.caseHal?._links.getLink('disableAutomation')?.href;
    if (!disableAutomationLink) {
      return;
    }
    this.loadingSpinnerService.show();
    this.httpClient.put<CaseHal>(disableAutomationLink, {}).subscribe({
      next: (caseHal: CaseHal) => {
        this.caseHal = new CaseHal(caseHal);
        this.selectedDocuments = [];
        this.updateDocumentsHal(this.paginator);
        this.snackBarService.open('caseDetail.snackbars.disableAutomationSuccess', 3);
        setTimeout(() => this.recalculateOverflow());
      },
      error: () => {
        this.loadingSpinnerService.hide();
        this.snackBarService.open('caseDetail.snackbars.disableAutomationError', 3, 'error');
      }
    });
  }

  protected openMoveCaseModal() {
    this.dialogService.open(MoveCaseComponent, {
      width: '40%',
      data: {
        currentTeamId: this.caseHal.teamId,
        caseName: this.caseHal.name
      }
    }).afterClosed().subscribe(chosenTeam => {
      if (chosenTeam) {
        this.moveCase(chosenTeam);
      }
    });
  }

  private moveCase(targetTeam: Team) {
    const moveLink = this.caseHal?._links.getLink('move')?.href;
    if (!moveLink) {
      return;
    }
    const sourceTeamId = this.caseHal.teamId;
    this.loadingSpinnerService.show();
    this.httpClient.post<CaseHal>(moveLink, {targetTeamId: targetTeam.identification}).subscribe({
      next: () => {
        this.snackBarService.open('caseDetail.snackbars.moveCaseSuccess', 3, undefined, {teamName: targetTeam.name});
        // The mover may not be a member of the target team (permission is intentionally open, FHCO-664), so
        // navigating to the moved case can 403. Go to the source team's case overview instead - taken from the
        // case rather than from the session, which only holds a team once the team selection page was visited.
        void this.router.navigate(['teams', sourceTeamId, 'cases']);
      },
      error: () => {
        this.loadingSpinnerService.hide();
        this.snackBarService.open('caseDetail.snackbars.moveCaseError', 3, 'error');
      }
    });
  }

  protected selectionChanged(identification: string) {
    if (this.selectedDocuments.includes(identification)) {
      this.selectedDocuments.splice(this.selectedDocuments.indexOf(identification), 1);
      return;
    }
    this.selectedDocuments.push(identification);
  }

  protected selectionChangeAll() {
    const allSelected = this.areAllDocumentsSelected();
    this.selectedDocuments = allSelected ? [] : this.documentHals.map(document => document.identification);
  }

  protected downloadSelectedDocuments() {
    this.loadingSpinnerService.show();
    if (!this.canSelectedDocumentsBeDownloaded()) {
      this.dialogService.openExceptionDialog({
        data: {
          title: 'addDocuments.canOnlyDownloadDocumentsWithPdf.subject',
          message: 'addDocuments.canOnlyDownloadDocumentsWithPdf.body'
        }
      });
      this.loadingSpinnerService.hide();
      return;
    }
    if (this.selectedDocuments.length === 1) {
      this.downloadAndOpenSinglePdf()
    } else if (this.selectedDocuments.length > 1) {
      this.downloadAndOpenMultiplePdfsAsZip();
    }
  }

  protected deleteSelectedDocuments() {
    if (this.isAutomatedCase()) {
      return;
    }
    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'caseDetail.deleteDocumentsDialog.title',
        message: this.translateService.instant('caseDetail.deleteDocumentsDialog.message', {documentCount: this.selectedDocuments.length}),
        confirmButtonText: 'caseDetail.deleteDocumentsDialog.confirm',
        cancelButtonText: 'caseDetail.deleteDocumentsDialog.cancel'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadingDocuments = true;
        let deleteObservable: Observable<object> = of();
        for (let identification of this.selectedDocuments) {
          deleteObservable = merge(deleteObservable, this.deleteDocument(identification));
        }
        deleteObservable.pipe(finalize(() => this.refreshCase())).subscribe(() => {
          this.selectedDocuments = [];
          this.updateDocumentsHal(this.paginator);
        });
      }
    });
  }

  protected canAllDocumentsBeDeleted(): boolean {
    return this.selectedDocuments.every(identification => {
      const document: DocumentHal | undefined = this.documentHals.find(doc => doc.identification === identification);
      return document?._links && new DocumentHal(document)._links.hasLink('delete');
    });
  }

  protected canSelectedDocumentsBeMarkedReady(): boolean {
    const selectedDocumentHals = this.getSelectedDocumentHals();
    if (selectedDocumentHals.length !== this.selectedDocuments.length) {
      return false;
    }
    return selectedDocumentHals.length > 0 && selectedDocumentHals.every(document => new DocumentHal(document)._links.hasLink('bulkReady'));
  }

  protected markSelectedDocumentsAsReady() {
    if (!this.canSelectedDocumentsBeMarkedReady()) {
      return;
    }
    this.loadingDocuments = true;
    from(this.getSelectedDocumentHals()).pipe(
      concatMap(document => this.bulkReadyDocument(document)),
      reduce((result, bulkReadyResult) => {
        if (bulkReadyResult === 'bulkReady') {
          result.bulkReady++;
        }
        if (bulkReadyResult === 'failed') {
          result.failed++;
        }
        if (bulkReadyResult === 'notBulkReady') {
          result.notBulkReady++;
        }
        return result;
      }, {bulkReady: 0, failed: 0, notBulkReady: 0})
    ).subscribe({
      next: (result) => {
        if (result.failed === 0 && result.notBulkReady === 0) {
          this.snackBarService.open('caseDetail.snackbars.setReadySuccess', 3);
        } else {
          this.snackBarService.open('caseDetail.snackbars.setReadyError', 3, 'error');
        }
        this.selectedDocuments = [];
        this.updateDocumentsHal(this.paginator);
        this.refreshCase();
      },
      error: () => {
        this.loadingDocuments = false;
        this.snackBarService.open('caseDetail.snackbars.setReadyError', 3, 'error');
      }
    });
  }

  protected canSelectedDocumentBeSealed(): boolean {
    return !this.loadingDocuments
      && this.selectedDocuments.length === 1
      && this.selectedDocumentHasSealLink();
  }

  protected anyDocumentHasSealLink(): boolean {
    return this.documentHals.some(document => new DocumentHal(document)._links.hasLink('seal'));
  }

  private selectedDocumentHasSealLink(): boolean {
    return this.getSelectedDocumentHals().some(document => new DocumentHal(document)._links.hasLink('seal'));
  }

  private isSelectedDocumentSealed(): boolean {
    return this.getSelectedDocumentHals().some(document => document.sealed);
  }

  private isSelectedDocumentReady(): boolean {
    return this.getSelectedDocumentHals().some(document => document.state === 'READY');
  }

  protected getSealSelectedDocumentTooltipKey(): string {
    if (this.loadingDocuments) {
      return '';
    }
    if (this.canSelectedDocumentBeSealed()) {
      return 'caseDetail.buttons.startSealingProcess.sealSelectedDocument';
    }
    if (this.selectedDocuments.length === 0) {
      return 'caseDetail.buttons.startSealingProcess.disabledTooltipNoSelection';
    }
    if (this.selectedDocuments.length > 1) {
      return 'caseDetail.buttons.startSealingProcess.disabledTooltipMultipleSelected';
    }
    if (this.isSelectedDocumentSealed()) {
      return 'caseDetail.buttons.startSealingProcess.disabledTooltipAlreadySealed';
    }
    if (!this.canSelectedDocumentsBeDownloaded()) {
      return 'caseDetail.buttons.startSealingProcess.disabledTooltipNoFile';
    }
    if (!this.isSelectedDocumentReady()) {
      return 'caseDetail.buttons.startSealingProcess.disabledTooltipNotReady';
    }
    return 'caseDetail.buttons.startSealingProcess.disabledTooltip';
  }

  protected sealSelectedDocument() {
    if (this.loadingDocuments) {
      return;
    }
    if (!this.canSelectedDocumentBeSealed()) {
      this.snackBarService.open('caseDetail.snackbars.sealError', 3, 'error');
      return;
    }
    const sealLink = this.getSelectedDocumentHals()
      .map(document => new DocumentHal(document)._links.getLink('seal')?.href)
      .find(href => !!href);
    if (!sealLink) {
      this.snackBarService.open('caseDetail.snackbars.sealError', 3, 'error');
      return;
    }
    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'caseDetail.sealDocumentDialog.title',
        message: 'caseDetail.sealDocumentDialog.message',
        confirmButtonText: 'caseDetail.sealDocumentDialog.confirm',
        cancelButtonText: 'caseDetail.sealDocumentDialog.cancel'
      }
    }).afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      this.loadingDocuments = true;
      this.httpClient.put<DocumentHal>(sealLink, {}).subscribe({
        next: () => {
          this.selectedDocuments = [];
          this.snackBarService.open('caseDetail.snackbars.sealSuccess', 3);
          this.updateDocumentsHal(this.paginator);
          this.refreshCase();
        },
        error: () => {
          this.loadingDocuments = false;
          this.snackBarService.open('caseDetail.snackbars.sealError', 3, 'error');
        }
      });
    });
  }

  protected canSelectedDocumentsBeDownloaded(): boolean {
    return this.selectedDocuments.every(identification => {
      const document: DocumentHal | undefined = this.documentHals.find(doc => doc.identification === identification);
      return !!document?.fileMetadata?.fileId;
    });
  }

  protected getDownloadSelectedDocumentsTooltipKey(): string {
    return this.canSelectedDocumentsBeDownloaded()
      ? 'caseDetail.tooltips.downloadSelectedDocuments'
      : 'caseDetail.tooltips.downloadSelectedDocumentsDisabled';
  }

  protected deleteSingleDocument(identification: string) {
    if (this.isAutomatedCase()) {
      return;
    }
    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'caseDetail.deleteDocumentDialog.title',
        message: this.translateService.instant('caseDetail.deleteDocumentDialog.message', {documentName: this.documentHals.find(document => document.identification === identification)?.title}),
        confirmButtonText: 'caseDetail.deleteDocumentDialog.confirm',
        cancelButtonText: 'caseDetail.deleteDocumentDialog.cancel'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadingDocuments = true;
        this.deleteDocument(identification).subscribe(() => {
            this.updateDocumentsHal(this.paginator);
          this.refreshCase();
          }
        );
      }
    })
  }

  protected restoreDocument(identification: string) {
    this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'caseDetail.restoreDocumentDialog.title',
        message: this.translateService.instant('caseDetail.restoreDocumentDialog.message', {documentName: this.documentHals.find(document => document.identification === identification)?.title}),
        confirmButtonText: 'caseDetail.restoreDocumentDialog.confirm',
        cancelButtonText: 'caseDetail.restoreDocumentDialog.cancel'
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.loadingDocuments = true;
        this.performDocumentRestore(identification).subscribe(() => {
            this.updateDocumentsHal(this.paginator);
          this.refreshCase();
          }
        );
      }
    })
  }

  private performDocumentRestore(identification: string) {
    return this.httpClient.put(new DocumentHal(this.documentHals.find(document => document.identification === identification))._links.getLink("restore")?.href!, "").pipe(
      tap({
        next: () => {
          this.snackBarService.open('caseDetail.snackbars.restoreSuccess', 3);
        },
        error: () => {
          this.snackBarService.open('caseDetail.snackbars.restoreError', 3, 'error');
        }
      })
    )
  }

  protected deleteDocument(identification: string) {
    return this.httpClient.delete(new DocumentHal(this.documentHals.find(document => document.identification === identification))._links.getLink("delete")?.href!).pipe(
      tap({
        next: () => {
          this.selectedDocuments.splice(this.selectedDocuments.indexOf(identification), 1);
          this.snackBarService.open('caseDetail.snackbars.deleteSuccess', 3);
        },
        error: () => {
          this.snackBarService.open('caseDetail.snackbars.deleteError', 3, 'error');
        }
      })
    )
  }

  protected navigateToDocumentPreview(document: DocumentHal) {
    const signProcessLink = document._links.getLink("signProcess")?.href;
    if (signProcessLink) {
      window.open(signProcessLink, '_blank');
      return;
    }

    if (this.isDocumentPreviewAvailable(document)) {
      const redirectRoute = document._links.getLink('self')?.getRoute();
      this.loadingSpinnerService.show();
      this.router.navigate([redirectRoute]).then();
    }
  }

  protected isDocumentPreviewAvailable(document: DocumentHal): boolean {
    return !!(document?.hasFileId() || document?.formIdentifier);
  }

  private getSelectedDocumentHals(): DocumentHal[] {
    return this.selectedDocuments
      .map(identification => this.documentHals.find(document => document.identification === identification))
      .filter((document): document is DocumentHal => !!document);
  }

  private bulkReadyDocument(document: DocumentHal): Observable<'bulkReady' | 'failed' | 'notBulkReady'> {
    if (document.state === 'READY') {
      return of('bulkReady');
    }
    const bulkReadyLink = new DocumentHal(document)._links.getLink('bulkReady')?.href;
    if (!bulkReadyLink) {
      return of('notBulkReady');
    }
    return this.httpClient.put<DocumentHal>(bulkReadyLink, {}).pipe(
      map(() => 'bulkReady' as const),
      catchError(() => of('failed' as const))
    );
  }

  protected downloadAndOpenSinglePdf(documentHal: DocumentHal | undefined = undefined) {
    this.loadingSpinnerService.show();
    documentHal ??= this.documentHals.find(document => document.identification === this.selectedDocuments[0]);
    if (documentHal) {
      this.httpClient.get<Blob>(new DocumentHal(documentHal)._links.getLink('file')?.href!, {responseType: "blob" as "json"}).subscribe((response: Blob) => {
        let blob = new Blob([response], {type: 'application/pdf'});
        let url = globalThis.URL.createObjectURL(blob);
        let anchor = document.createElement("a");
        anchor.download = documentHal.title;
        anchor.href = url;
        anchor.click();
        this.loadingSpinnerService.hide();
      });
      return;
    }
    this.loadingSpinnerService.hide();
    this.snackBarService.open('caseDetail.snackbars.downloadError', 3, 'error');
  }

  protected backToCasesOverview() {
    this.loadingSpinnerService.show();
    this.router.navigate([this.sessionService.getSession().currentTeam!._links.getLink('caseOverview')?.getRoute()]).then();
  }

  protected completeCase() {
    this.loadingSpinnerService.show();
    this.router.navigate([this.caseHal._links.getLink('completeCase')?.getRoute()]).then();
  }

  protected openAddFormsDialog() {
    this.dialogService.open(AddFormsDialogComponent, {
      data: {
        caseHal: this.caseHal,
        formType: "AF"
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.snackBarService.open('caseDetail.snackbars.addFormsSuccess', 3);
      }
      if (result === false) {
        this.snackBarService.open('caseDetail.snackbars.addFormsError', 3, 'error');
      }
      this.updateDocumentsHal(this.paginator);
      this.refreshCase();
    });
  }

  protected fileSelected($event: Event) {
    // Read and clear in one step: the flag must not survive a cancelled file dialog and mark the next upload.
    const asCompletionDocument = this.uploadAsCompletionDocument;
    this.uploadAsCompletionDocument = false;
    if (!this.canUploadDocuments()) {
      return;
    }
    this.uploading = true;
    this.loadingDocuments = true;
    const target = $event.target as HTMLInputElement;
    let documentsToUpload: DocumentDtoModel[] = [];
    let hasErrors = false;
    if (target.files) {
      for (let i = 0; i < target.files.length; i++) {
        const file = target.files[i];
        // check if file is an allowed file type
        if (!AllowedFileTypeService.allowedFileTypesForFileUpload.includes(file.type)) {
          this.snackBarService.open(this.translateService.instant('addDocument.error.fileType', {fileName: file.name}), 10, 'error');
          hasErrors = true;
          break;
        }
        if (file.size > 10485760) { // 10 MB
          this.snackBarService.open(this.translateService.instant('addDocument.error.fileSize', {fileName: file.name}), 5, 'error');
          hasErrors = true;
          break;
        }
        if (file.name.includes('$!$')) {
          this.snackBarService.open(this.translateService.instant('addDocument.error.sneakyDevFileName', {fileName: file.name}), 10, 'error');
          hasErrors = true;
          break;
        }
        this.files.push(file);
        let documentToUpload = new DocumentDtoModel({});
        documentToUpload.title = file.name;
        if (asCompletionDocument) {
          documentToUpload.type = COMPLETION_DOCUMENT_TYPE;
        }
        documentToUpload.setFileId(file.name + '$!$' + file.size + '$!$' + file.lastModified);
        documentsToUpload.push(documentToUpload);
      }
      if (documentsToUpload.length > 0 && !hasErrors) {
        this.uploadDocuments(documentsToUpload);
      } else {
        this.uploading = false;
        this.loadingDocuments = false;
        this.files = [];
      }
      // Required to be able to upload the same file again.
      target.value = '';
      return;
    }
    this.loadingDocuments = false;
    this.uploading = false;
  }

  protected fileDropped($event: any) {
    if (!this.canAddRegularDocuments()) {
      return;
    }
    document.getElementById('pdfUploadContainer')?.classList.remove('dragOver');
    const inputEvent = {target: {files: $event}};
    this.fileSelected(inputEvent as unknown as Event);
  }

  private uploadDocuments(documentsToUpload: DocumentDtoModel[]) {
    this.loadingDocuments = true;
    from(documentsToUpload).pipe(
      concatMap(document => {
        const addDocumentsLink = this.caseHal?._links.getLink('addDocuments')?.href;
        return this.httpClient.post<CaseHal>(
          <string>addDocumentsLink,
          this.documentHelperService.getMultiPartForDocument(document,
            this.files.find(file => file.name === document.getFileId()?.split('$!$')[0]))
        );
      }),
      last()
    ).subscribe({
      next: () => {
        this.snackBarService.open('caseDetail.snackbars.uploadSuccess', 3);
        this.uploading = false;
        this.files = [];
        this.updateDocumentsHal(this.paginator);
        this.refreshCase();
      },
      error: () => {
        this.snackBarService.open('caseDetail.snackbars.uploadError', 3, 'error');
        this.loadingDocuments = false;
        this.uploading = false;
      }
    });
  }

  private downloadAndOpenMultiplePdfsAsZip() {
    this.httpClient.post<Blob>(this.caseHal._links.getLink("compressedFiles")?.href!, this.selectedDocuments, {responseType: "blob" as "json"}).subscribe({
      next: (response: Blob) => {
        let blob = new Blob([response], {type: 'application/octet-stream'});
        let url = globalThis.URL.createObjectURL(blob);
        let anchor = document.createElement("a");
        anchor.download = buildCaseZipFilename(this.caseHal, this.getSelectedDocumentHals(), this.selectedDocuments.length);
        anchor.href = url;
        anchor.click();
        this.loadingSpinnerService.hide();
      },
      error: () => {
        this.loadingSpinnerService.hide();
        this.snackBarService.open('caseDetail.snackbars.downloadError', 3, 'error');
      }
    });
  }

  protected pdfUploadDragEnter() {
    if (this.canUploadDocuments() && !this.uploading && !this.loadingDocuments && this.documentHals.length > 0) {
      document.getElementById('pdfUploadContainer')?.classList.add('dragOver');
    }
  }

  protected pdfUploadDragLeave($event: DragEvent) {
    const pdfUploadContainer = document.getElementById('pdfUploadContainer');
    // Check if the drag leave event is coming from a child element of the pdfUploadContainer and if so don't remove the class.
    if (!pdfUploadContainer?.contains($event.relatedTarget as Node)) {
      pdfUploadContainer?.classList.remove('dragOver');
    }
  }

  protected isValidUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return ['http:', 'https:'].includes(parsedUrl.protocol);
    } catch {
      return false;
    }
  }

  protected showCaseRequestInformationButton(): boolean {
    const reason = this.caseHal.requestInformationDisabledReason;

    if (reason === RequestInformationDisabledReasonEnum.FEATURE_DISABLED) {
      return false;
    }

    const hasReason = !!reason;
    const hasLink = this.caseHal._links.hasLink('requestInformation');

    return hasReason || hasLink;
  }

  protected getRequestInformationTooltipKey(): string {
    if (this.caseHal._links.hasLink('requestInformation')) {
      return '';
    }
    if (this.caseHal.requestInformationDisabledReason === RequestInformationDisabledReasonEnum.FEATURE_DISABLED) {
      return '';
    }
    switch (this.caseHal.requestInformationDisabledReason) {
      case RequestInformationDisabledReasonEnum.CLIENT_EMAIL_REQUIRED:
        return 'caseDetail.buttons.requestInformation.disabledTooltipEmailRequired';
      case RequestInformationDisabledReasonEnum.CLIENT_EXTERNAL_USER_ID_REQUIRED:
        return 'caseDetail.buttons.requestInformation.disabledTooltipExternalUserIdRequired';
      default:
        return 'caseDetail.buttons.requestInformation.disabledTooltip';
    }
  }


  protected areAllDocumentsSelected(): boolean {
    return this.documentHals.length > 0
      && this.documentHals.every(document => this.selectedDocuments.includes(document.identification));
  }

  protected areSomeDocumentsSelected(): boolean {
    const selectedDocumentCount = this.documentHals
      .filter(document => this.selectedDocuments.includes(document.identification))
      .length;
    return selectedDocumentCount > 0 && selectedDocumentCount < this.documentHals.length;
  }

  // Document mutations (set ready, delete, restore, seal, add, upload) and custom actions can transition the case state, so the case has to be re-fetched.
  private refreshCase() {
    const requestId = ++this.caseRequestId;
    this.httpClient.get<CaseHal>(this.caseHal._links.getLink('self')?.href!).subscribe({
      next: (caseHal: CaseHal) => {
        if (this.destroyed || requestId !== this.caseRequestId) return;
        this.caseHal = new CaseHal(caseHal);
        this.commentContextService.setPanelHal(this.caseHal);
        setTimeout(() => this.recalculateOverflow());
      },
      error: () => {
        if (this.destroyed || requestId !== this.caseRequestId) return;
        this.snackBarService.open('caseDetail.snackbars.loadError', 3, 'error');
      }
    });
  }
}
